import { useState, useEffect, useContext, useRef } from 'react';
import { Button, Form, Row, Col, Card, Badge, InputGroup } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';

import PortalService from '../../../services/portal.service';
import StateContext from '../../../context/State';
import PurchasePropertyDetails from '../PurchasePropertyDetails';
import SelectiveMerge from './SelectiveMerge';
import AddressAutocomplete from '../../../components/AddressAutocomplete';
import { runInnerFunction } from '../../../../../compatManager';
import { ClientIntakeLoadingComponent, SingleClient } from '../../../components/LoadingComponents';
import { Buyer, ChangesModel, Client, CustomField, FileInfoModel, IntakeState, Place, Seller } from '../../../types';
import { DefaultIntakeState } from '../../../data';
import { contactRenames, fileContactNames } from '../../../../../../../share/utils/perClientCustomFields';
import { registerFileContacts } from '../../../../../../../share/utils/contactIdentity';

import ClientDetails from './ClientDetails';
import { RPWDataReader, getDataReader } from '../../../../../livedataFuncs';
import { useAppSelector } from '../../../../../redux/hooks';
import { lddStateObject } from '../../../../../redux/reducer';
import styles from './connectHeader.module.scss';
import { lddGlobalSessionInfo } from '../../../../navigationTypes';
import { displayToastMsg } from '../../../../toastMessages/toastmessages';

type TProps = {
    setResentUpdate: (newVal: number) => void
}

const ClientIntake = (props: TProps) => {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    let DataReaderObj: RPWDataReader;
    DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    const { setResentUpdate } = props;
    const sessionInfo: lddGlobalSessionInfo = useAppSelector((state) => state.SessionInfo);

    const [acceptedChanges, setAcceptedChanges] = useState<any[]>([]); // todo: type
    const [showModal, setShowModal] = useState<boolean>(false);
    const [touched, setTouched] = useState<string[]>([]);
    const [rerender, setRerender] = useState<number>(0);
    const [addressChanged, setAddressChanged] = useState<number>(1);
    const [buyers, setBuyers] = useState<Buyer[]>([]);
    const [sellers, setSellers] = useState<Seller[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [selectiveMergeChanges, setSelectiveMergeChanges] = useState<ChangesModel[]>([]);
    const [intakeState, setIntakeState] = useState<IntakeState>(DefaultIntakeState);
    const [customFieldValues, setCustomFieldValues] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [fileData, setFileData] = useState<FileInfoModel>();   
    // The file as stored, before pending client changes are laid over it.
    const storedFileRef = useRef<any>(null);

    useEffect(() => {
        portalService.getFileInfo()
            .then((data: FileInfoModel) => {

                if (!fileData){
                    setFileData(data);
                }
                const { changes } = data;
                storedFileRef.current = data.file;
                const storedNames = fileContactNames(data.file);
                const file = JSON.parse(JSON.stringify(data.file));

                // Dispatch changes
                for (let i = 0; i < changes.length; i++) {
                    const change = changes[i];
                    if (change.FieldName.includes('-')) {
                        // Array field changed
                        const field = change.FieldName.split('-');
                        // If added new, add array for them
                        if (!file[field[0]][field[1]]) {
                            file[field[0]][field[1]] = [];
                        }
                        // Add changes to array fields
                        file[field[0]][field[1]][field[2]] = change.NewValue === "" ? null : change.NewValue;
                    } else {
                        file[change.FieldName] = change.NewValue === "" ? null : change.NewValue;
                    }
                }


                // Fixes the key each contact's custom field values are read under for this
                // page load, before any name is edited.
                registerFileContacts(file, storedNames);

                // Seting values
                setClients(file.clients)
                setBuyers(file.buyers);
                setSellers(file.sellers);
                setIntakeState({
                    propertyType: file.propertyType,
                    propertyAddress: setPropAddress(file),
                    streetNumber: file.streetNumber,
                    streetNumberSuffix: file.streetNumberSuffix,
                    streetName: file.streetName,
                    unitType: file.unitType,
                    unitNumber: file.unitNumber,
                    city: file.city,
                    postalCode: file.postalCode,
                    closingDate: file.closingDate ? new Date(file.closingDate + ' :10:00:00') : null, // Add 10 hours so timezone change go well
                    propertyPrice: file.propertyPrice,
                    mortgageFile: file.clients.length !== 0,
                    dataLoaded: true,
                    province: portalService.getFullProvince()
                })

                setAddressChanged(addressChanged + 1);

                // Mark fields with changes
                setTouched(data.changes.map(c => (c.FieldName)));

                // Set changes for selective merge
                setSelectiveMergeChanges(changes);
                setLoading(false);
                portalService.updateClientList(state.portalId);
            })
            .catch(err => { console.log(err) });
    }, [rerender]);


    const handlePropertyTypeChange = (value) => {
        setIntakeState({
            ...intakeState,
            propertyType: value
        })
        setAddressChanged(addressChanged + 1);
    }


    const setPropAddress = (file): string => {
        let address = '';

        if (file.streetNumber !== '' && file.streetNumber !== null) {
            address = file.streetNumber;
        }
        if (file.streetNumberSuffix !== '' && file.streetNumberSuffix !== null) {
            address += file.streetNumberSuffix;
        }
        if (file.streetName !== '' && file.streetName !== null) {
            address += ' ' + file.streetName;
        }
        if (file.propertyType === 'Condo' && file.unitType !== '' && file.unitType !== null) {
            address += ', ' + file.unitType;
        }
        if (file.propertyType === 'Condo' && file.unitNumber !== '' && file.unitNumber !== null) {
            address += ' ' + file.unitNumber;
        }
        if (file.city !== '' && file.city !== null) {
            address += ', ' + file.city;
        }

        address += ", " + portalService.getFullProvince();

        if (file.postalCode !== '' && file.postalCode !== null) {
            address += ' ' + file.postalCode;
        }
        return address;
    }


    const handlePropertyAddressChange = (place: Place) => {
        let newIntakeState = { ...intakeState }
        for (var i = 0; i < place.address_components.length; i++) {
            for (var k = 0; k < place.address_components[i].types.length; k++) {
                switch (place.address_components[i].types[k]) {
                    case 'street_number':
                        newIntakeState.streetNumber = place.address_components[i]?.long_name ?? '';
                        break;
                    case 'postal_code':
                        newIntakeState.postalCode = place.address_components[i]?.long_name ?? '';
                        break;
                    case 'route':
                        newIntakeState.streetName = place.address_components[i]?.long_name ?? '';
                        break;
                    case 'locality':
                        newIntakeState.city = place.address_components[i]?.long_name ?? '';
                        break;
                }
            }
        }
        setIntakeState(newIntakeState)
        setAddressChanged(addressChanged + 1);
    }

    useEffect(() => {
        // Compute property address on change
        let address = '';

        if (intakeState.streetNumber !== '' && intakeState.streetNumber !== null) {
            address = intakeState.streetNumber;
        }
        if (intakeState.streetNumberSuffix !== '' && intakeState.streetNumberSuffix !== null) {
            address += intakeState.streetNumberSuffix;
        }
        if (intakeState.streetName !== '' && intakeState.streetName !== null) {
            address += ' ' + intakeState.streetName;
        }
        if (intakeState.propertyType === 'Condo' && intakeState.unitType !== '' && intakeState.unitType !== null) {
            address += ', ' + intakeState.unitType;
        }
        if (intakeState.propertyType === 'Condo' && intakeState.unitNumber !== '' && intakeState.unitNumber !== null) {
            address += ' ' + intakeState.unitNumber;
        }
        if (intakeState.city !== '' && intakeState.city !== null) {
            address += ', ' + intakeState.city;
        }

        address += ", " + portalService.getFullProvince();
        if (intakeState.postalCode !== '' && intakeState.postalCode !== null) {
            address += ' ' + intakeState.postalCode;
        }

        setIntakeState({
            ...intakeState,
            propertyAddress: address
        })
    }, [addressChanged]);

    const handleSubmit = () => {
        //event.preventDefault();
        // TODO
        mergeToFile();
    };

    const handleModal = (modalState) => {
        setAcceptedChanges([]);
        setShowModal(modalState);
    }

    // Before the file is saved, per-client custom field values are copied under any contact
    // name this import changes, so the cards still find them under the new name. Best
    // effort: the import goes ahead whatever happens here.
    const copyValuesToRenamedContacts = async (renames: Array<{ from: string; to: string }>) => {
        if (renames.length === 0) {
            return;
        }
        try {
            await portalService.copyPerClientCustomFields(state.portalId, renames);
        } catch (error) {
            console.error('Unable to copy per-client custom fields to the new names', error);
        }
    };

    const mergeToFile = async () => {
        // Get changes
        const newFile = getNewFile();
        const renames = contactRenames(storedFileRef.current, newFile);
        await copyValuesToRenamedContacts(renames);

        runInnerFunction(
            'ClientPortalUpdateAndSaveFile',
            {
                newFile,
                province: portalService.getProvince()
            }
        );

        // Renames the portal clients in place, keeping the Ids their sessions are tied to.
        // A by-name sync (updateClientList, run again on reload) before this finishes would
        // replace a renamed client's row and sign them out. Not gated on the save's result:
        // ClientPortalUpdateAndSaveFile returns false even when the save succeeds.
        await portalService.updateClientNames(state.portalId, renames).catch(() => undefined);
        // File updated, delete changes from DB
        portalService.deleteChanges()
            .then(_response => {
                setRerender(rerender + 1);
                setSellers([]);
                setBuyers([]);
                setClients([]);
            })
            .catch(() => { console.log('err') });
    }

    const getNewFile = (): any => {
        let newFile = {};

        if (acceptedChanges.length > 0) {
            // Selective merge
            for (let i = 0; i < acceptedChanges.length; i++) {
                const change = acceptedChanges[i];
                if (change.includes('-')) {
                    // Array field changed, add whole array and apply changes
                    const field = change.split('-');
                    //newFile['buyers'] = buyers;

                    if (!newFile[field[0]]) {
                        newFile[field[0]] = [];
                    }
                    if (!newFile[field[0]][field[1]]) {
                        newFile[field[0]][field[1]] = {};
                    }
                    // Add changes to array fields
                    if (buyers.length !== 0) {
                        newFile[field[0]][field[1]][field[2]] = buyers[field[1]][field[2]];

                        // Format date field
                        if (field[2] === 'birthDate' && buyers[field[1]][field[2]] !== '') {
                            newFile[field[0]][field[1]][field[2]] = (buyers[field[1]][field[2]]) !== null ? (buyers[field[1]][field[2]]).toISOString().slice(0, 10) : null;
                        }
                    }

                    if (sellers.length !== 0) {
                        newFile[field[0]][field[1]][field[2]] = sellers[field[1]][field[2]];

                        // Format date field
                        if (field[2] === 'birthDate' && sellers[field[1]][field[2]] !== '') {
                            newFile[field[0]][field[1]][field[2]] = (sellers[field[1]][field[2]]) !== null ? (sellers[field[1]][field[2]]).toISOString().slice(0, 10) : null;
                        }
                    }

                    if (clients.length !== 0) {
                        newFile[field[0]][field[1]][field[2]] = clients[field[1]][field[2]];

                        // Format date field
                        if (field[2] === 'birthDate' && clients[field[1]][field[2]] !== '') {
                            newFile[field[0]][field[1]][field[2]] = (clients[field[1]][field[2]]) !== null ? (clients[field[1]][field[2]]).toISOString().slice(0, 10) : null;
                        }
                    }

                } else {
                    let value = '';

                    switch (change) {
                        case 'propertyType':
                            value = intakeState.propertyType;
                            break;
                        case 'propertyAddress':
                            value = intakeState.propertyAddress;
                            break;
                        case 'streetNumber':
                            value = intakeState.streetNumber;
                            break;
                        case 'streetNumberSuffix':
                            value = intakeState.streetNumberSuffix;
                            break;
                        case 'streetName':
                            value = intakeState.streetName;
                            break;
                        case 'unitType':
                            value = intakeState.unitType;
                            break;
                        case 'unitNumber':
                            value = intakeState.unitNumber;
                            break;
                        case 'city':
                            value = intakeState.city;
                            break;
                        case 'postalCode':
                            value = intakeState.postalCode;
                            break;
                        case 'closingDate':
                            value = intakeState.closingDate ? intakeState.closingDate.toISOString().slice(0, 10) : '';
                            break;
                        case 'propertyPrice':
                            value = intakeState.propertyPrice;
                            break;
                    }

                    newFile[change] = value;
                }

                if (buyers.length !== 0 && newFile['buyers'] && Array.isArray(newFile['buyers'])) {
                    for (let j = 0; j < buyers.length; j++) {
                        if (buyers.length > newFile['buyers'].length) {
                            newFile['buyers'].push(null)
                        }
                    }
                }

                if (sellers.length !== 0 && newFile['sellers'] && Array.isArray(newFile['sellers'])) {
                    for (let j = 0; j < sellers.length; j++) {
                        if (sellers.length > newFile['sellers'].length) {
                            newFile['sellers'].push(null)
                        }
                    }
                }

                if (clients.length !== 0 && newFile['clients'] && Array.isArray(newFile['clients'])) {
                    for (let j = 0; j < clients.length; j++) {
                        if (clients.length > newFile['clients'].length) {
                            newFile['clients'].push(null)
                        }
                    }
                }



            }
        } else {
            // Regular merge
            newFile = {
                buyers
                    : buyers.map(buyer => ({
                        ...buyer,
                        birthDate: Object.hasOwn(buyer, 'birthDate') ? ((buyer.birthDate !== null && buyer.birthDate.toString() !== '') ? new Date(buyer.birthDate).toISOString().slice(0, 10) : null) : null
                    })),
                sellers
                    : sellers.map(seller => ({
                        ...seller,
                        birthDate: Object.hasOwn(seller, 'birthDate') ? ((seller.birthDate !== null && seller.birthDate.toString() !== '') ? new Date(seller.birthDate).toISOString().slice(0, 10) : null) : null

                    })),
                clients
                    : clients.map(client => ({
                        ...client,
                        birthDate: Object.hasOwn(client, 'birthDate') ? ((client.birthDate !== null && client.birthDate.toString() !== '') ? new Date(client.birthDate).toISOString().slice(0, 10) : null) : null
                    })),
                propertyType: intakeState.propertyType,
                propertyAddress: intakeState.propertyAddress,
                streetNumber: intakeState.streetNumber,
                streetNumberSuffix: intakeState.streetNumberSuffix,
                streetName: intakeState.streetName,
                unitType: intakeState.unitType,
                unitNumber: intakeState.unitNumber,
                city: intakeState.city,
                postalCode: intakeState.postalCode,
                closingDate: intakeState.closingDate ? intakeState.closingDate.toISOString().slice(0, 10) : '',
                propertyPrice: intakeState.propertyPrice,
            }

        }

        return newFile;
    }

    const deleteChanges = () => {
        portalService.deleteChanges()
            .then(_response => { setRerender(rerender + 1); })
            .catch(() => { console.log('err') });
    }

    const getIncludeFirmEmail = async (sid: string): Promise<boolean> => {
        try {
            const res = await fetch(`getprofile.asp?pname=IncludeFirmEmail&SessionID=${ sid }`);
            const textData = await res.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(textData, 'text/xml');
            const includeEmailValue = xmlDoc.getElementsByTagName('IncludeEmail')[0]?.childNodes[0]?.nodeValue;
            if (includeEmailValue !== undefined) {
                return includeEmailValue === '1';
            } else {
                return false; // Default value if not set
            }
        } catch (e) {
            console.log('Unable to get Include Firm Email setting: ', e);
            return false;
        }
    };

    const 
    handleReSendInvitation = async (): Promise<void> => {
        try {
            const sid = sessionInfo.sid;
            const includeFirmEmail = await getIncludeFirmEmail(sid);
            const response = await portalService.resendInvitation(state.portalId, includeFirmEmail);
            if (response.message === 'error') {
                displayToastMsg({ id: null, msg: <p>Email failed to send</p>, type: "error" }, null);
                return;
            }
            alert('Invitation Re-sent')
            const province = await portalService.getProvince()

            const emailInfos: string[] = DataReaderObj.getClientContactInfo(province);

            for (const email of emailInfos) {

                const logData = {
                    appId: state.appId,
                    from: 'noreply@ldd.ca',
                    to: email,
                    ccEmail: '',
                    bccEmail: '',
                    subject: 'New reminder',
                    message: 'Your lawyer has invited you to clientConnect',
                    user: ""
                };

                try {
                    // Log and send the email for each address
                    await portalService.logEmail(state.appId, logData);
                } catch (error) {
                    console.error(`Failed to notify client via email at ${email}`, error);
                }
            }
            setResentUpdate(new Date().getTime());
            // window.location.reload();
        } catch (e) {
            console.log('Unable to resend invitation: ', e)
        }

    }

    const handleCustomFieldsChange = (newCustomFieldValues: CustomField[]) => {
        setCustomFieldValues(newCustomFieldValues);
    }

    if (loading) {
        return <ClientIntakeLoadingComponent />
    }


    return (
        <>
            <Card className={styles.header}>
                <h2>
                    <i className="fak fa-connect-solid"></i>
                    Client Intake Information
                </h2>

                <div>
                    <Button
                        className={styles.outlinePrimary}
                        onClick={() => handleReSendInvitation()}
                    >
                        Send New Invite
                    </Button>
                    <Button
                        className={styles.outlinePrimary}
                        onClick={() => handleModal(true)}
                    >
                        Selective Import
                    </Button>
                    <SelectiveMerge
                        showModal={showModal}
                        changes={selectiveMergeChanges}
                        onHideModal={() => handleModal(false)}
                        acceptedChanges={acceptedChanges}
                        setAcceptedChanges={setAcceptedChanges}
                        acceptChanges={mergeToFile}
                        rejectChanges={deleteChanges}
                    />
                    <Button
                        className={styles.primary}
                        onClick={() => handleSubmit()}
                    >
                        Import to File
                    </Button>
                </div>
            </Card>

            <Form>
                {intakeState.dataLoaded
                    ?
                    <>
                        <ClientDetails
                            province={intakeState.province}
                            buyers={buyers}
                            sellers={sellers}
                            clients={clients}
                            touched={touched}
                            onBuyersChange={setBuyers}
                            onSellersChange={setSellers}
                            onClientsChange={setClients}
                            customFields={customFieldValues}
                            onCustomFieldsChange={handleCustomFieldsChange}
                            fileInfo={fileData.file}
                        />
                    </>

                    :
                    <ClientIntakeLoadingComponent />
                }
                {
                    !intakeState.mortgageFile &&
                    <>
                        {
                            intakeState.dataLoaded ?
                                <>
                                    <Card>
                                        <Card.Header>
                                            <Card.Title>Property Information</Card.Title>
                                        </Card.Header>

                                        <Card.Body>
                                            <Row className="mb-3">
                                                <Col className="col-8">
                                                    <Form.Group controlId='searchAddress'>
                                                        <Form.Label>Address</Form.Label>
                                                        <InputGroup>
                                                            <InputGroup.Text>
                                                                <i className="fa-solid fa-house status-secondary-light" />
                                                            </InputGroup.Text>
                                                            <AddressAutocomplete
                                                                currAddress={intakeState.propertyAddress}
                                                                saveAddress={handlePropertyAddressChange}
                                                            />
                                                        </InputGroup>
                                                        {touched.includes('streetName') ? <Badge bg="info" text="dark" pill>Pending</Badge> : ''}
                                                    </Form.Group>
                                                </Col>

                                                {intakeState.province !== 'Saskatchewan' && (
                                                    <Col className="col-4">
                                                        <Form.Label>Type</Form.Label>
                                                        <Form.Select
                                                            name='propertyType'
                                                            value={intakeState.propertyType}
                                                            onChange={(event) => { handlePropertyTypeChange(event.target.value) }}
                                                        >
                                                            {
                                                                intakeState.province === 'Alberta' &&
                                                                <>
                                                                    <option value=''>-- No Type of Property --</option>
                                                                    <option value='Freehold (Fee Simple)'>Freehold (Fee Simple)</option>
                                                                    <option value='Condo'>Condominium (Freehold)</option>
                                                                    <option value='Leasehold'>Leasehold</option>
                                                                    <option value='Subleasehold'>Subleasehold</option>
                                                                    <option value='CondoSubleasehold'>Condominium (Subleasehold)</option>
                                                                </>
                                                            }
                                                            {
                                                                intakeState.province === 'British Columbia' &&
                                                                <>
                                                                    <option value=''>-- No Type of Property --</option>
                                                                    <option value='Freehold (Fee Simple)'>Freehold (Non-Strata)</option>
                                                                    <option value='Condo'>Strata (Fee Simple)</option>
                                                                    <option value='Leasehold'>Leasehold (Non-Strata)</option>
                                                                </>
                                                            }
                                                            {
                                                                intakeState.province !== 'Alberta' &&
                                                                intakeState.province !== 'British Columbia'
                                                                &&
                                                                <>
                                                                    <option value=''>-- No Type of Property --</option>
                                                                    <option value='Freehold (Fee Simple)'>Freehold (Fee Simple)</option>
                                                                    <option value='Condo'>Condominium (Freehold)</option>
                                                                </>
                                                            }
                                                        </Form.Select>
                                                        {touched.includes('propertyType') ? <Badge bg="info" text="dark" pill>Pending</Badge> : ''}
                                                    </Col>
                                                )}
                                            </Row>

                                            <Row className="mb-3">
                                                <Col className="col-4">
                                                    <Form.Group>
                                                        <Form.Label>Property Price</Form.Label>
                                                        <InputGroup>
                                                            <InputGroup.Text>
                                                                <i className="fa-solid fa-dollar-sign status-secondary-light" />
                                                            </InputGroup.Text>
                                                            <Form.Control
                                                                name='propertyPrice'
                                                                value={intakeState.propertyPrice}
                                                                onChange={(event) => {
                                                                    setIntakeState({
                                                                        ...intakeState,
                                                                        propertyPrice: event.target.value
                                                                    })
                                                                }}
                                                            />
                                                        </InputGroup>
                                                    </Form.Group>
                                                    {touched.includes('propertyPrice') ? <Badge bg="info" text="dark" pill>Pending</Badge> : ''}
                                                </Col>
                                            </Row>

                                            <Row className="col-12">
                                                <Col>
                                                    <PurchasePropertyDetails />
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </>
                                :
                                <SingleClient />
                        }
                    </>
                }
            </Form>
        </>
    );
}

export default ClientIntake;
